import React, { FC, useState } from 'react';
import z from 'zod';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@fiverr-private/button';
import { Modal, ModalBody, ModalCloseBtn, ModalContent, ModalFooter, ModalHeader } from '@fiverr-private/overlay';
import {
    Form,
    FormControl,
    FormCounter,
    FormElement,
    FormHeader,
    FormHelperText,
    FormLabel,
    FormOptionalLabel,
    Input,
    Radio,
    RadioGroup,
    Textarea,
    zodResolver,
} from '@fiverr-private/forms';
import { I18n, translate as t } from '@fiverr-private/i18n-react';
import { raiseToast } from '@fiverr-private/toast';
import { Text } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { atoms } from '@fiverr-private/theme';
import { logger } from '@fiverr-private/obs';
import { T_PREFIX } from '../../../lib/constants';
import { bqEvents, sendBqEvent } from '../../../lib/bq';
import { DisplayMode, EnrichedRecommendationItem } from '../../../lib/types';
import { metric } from '../../../lib/metric';
import { COMPONENT_NAME } from '../RecommendationsList/constants';
import { saveItemsToList } from './api';
import SaveButton from './SaveButton';

const T_KEY = `${T_PREFIX}.save_to_list`;

interface SaveToListButtonProps {
    items: EnrichedRecommendationItem[];
    displayMode: DisplayMode;
    listId: string;
}

export const SaveToListButton: FC<SaveToListButtonProps> = ({ items, displayMode, listId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { organizationId, userId } = getContext();

    const requiredNameError = t(`${T_KEY}.name_error_message`);
    const schema = z.object({
        name: z.string().min(2, { message: requiredNameError }).max(60, { message: requiredNameError }),
        description: z.string().max(330),
        ownerType: z.union([z.literal('USER'), z.literal('BUSINESS')]),
    });

    const methods = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            ownerType: 'USER',
        },
    });

    const close = () => {
        setIsOpen(false);
        setTimeout(() => {
            methods.reset();
            setIsLoading(false);
        }, 400);
    };

    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        const { description, name, ownerType } = data;
        try {
            sendBqEvent(
                bqEvents.saved_list({
                    buyerId: userId,
                    elementName: `save_${displayMode}_to_list`,
                    componentName: COMPONENT_NAME,
                    listId,
                })
            );

            setIsLoading(true);
            await saveItemsToList({
                displayMode: displayMode === DisplayMode.Gigs ? DisplayMode.Gigs : DisplayMode.Sellers,
                description,
                name,
                businessId: organizationId,
                items,
                ownerType,
            });
            raiseToast({
                type: 'success',
                location: 'top',
                message: t(`${T_KEY}.list_saved`, { params: { listName: data.name } }),
            });

            logger.info('List saved', { listName: data.name });
            metric.count('list_saved.success');
        } catch (error) {
            logger.error('Failed to save list', { listName: data.name });
            metric.count('list_saved.error');
            raiseToast({
                type: 'error',
                location: 'top',
                message: t(`${T_KEY}.error_saving`),
            });
        } finally {
            close();
        }
    };

    return (
        <Modal onClose={close} isOpen={isOpen}>
            <SaveButton setIsOpen={setIsOpen} />
            <ModalContent data-testid="save-to-list-modal">
                <ModalHeader>
                    <I18n k={`${T_KEY}.title`} />
                    <Text marginTop="1" marginLeft="0.5">
                        <I18n k={`${T_KEY}.subtitle`} />
                    </Text>
                    <ModalCloseBtn />
                </ModalHeader>
                <Form
                    methods={methods}
                    onSubmit={onSubmit}
                    className={atoms({
                        display: 'contents',
                    })}
                >
                    <ModalBody>
                        <FormControl maxLength={60} name="name">
                            <FormHeader>
                                <FormLabel htmlFor="name">
                                    <I18n k={`${T_KEY}.list_name`} />
                                </FormLabel>
                            </FormHeader>
                            <FormElement>
                                <Input placeholder={t(`${T_KEY}.name_placeholder`)} />
                            </FormElement>
                            <FormHelperText />
                            <FormCounter />
                        </FormControl>
                        <FormControl maxLength={330} name="description">
                            <FormHeader>
                                <FormLabel htmlFor="description">
                                    <I18n k={`${T_KEY}.description`} />
                                </FormLabel>
                                <FormOptionalLabel />
                            </FormHeader>
                            <FormElement>
                                <Textarea
                                    rows={5}
                                    placeholder={t(`${T_KEY}.description_placeholder`)}
                                    maxLength={330}
                                />
                            </FormElement>
                            <FormCounter />
                            <FormHelperText />
                        </FormControl>
                        {organizationId && (
                            <FormControl name="ownerType">
                                <FormHeader>
                                    <FormLabel>
                                        <I18n k={`${T_KEY}.who_can_edit`} />
                                    </FormLabel>
                                </FormHeader>
                                <FormElement>
                                    <RadioGroup name="ownerType">
                                        <Radio value="USER">
                                            <I18n k={`${T_KEY}.just_me`} />
                                        </Radio>
                                        <Radio value="BUSINESS">
                                            <I18n k={`${T_KEY}.the_entire_team`} />
                                        </Radio>
                                    </RadioGroup>
                                </FormElement>
                            </FormControl>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="outline" onClick={close} type="button">
                            <I18n k={`${T_KEY}.cancel`} />
                        </Button>
                        <Button type="submit" loading={isLoading}>
                            <I18n k={`${T_KEY}.create_list`} />
                        </Button>
                    </ModalFooter>
                </Form>
            </ModalContent>
        </Modal>
    );
};
